import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, message, Modal } from 'antd';
import { ExclamationCircleOutlined, UserOutlined, UpOutlined, DownOutlined, BarChartOutlined, FolderOutlined, SettingOutlined } from '@ant-design/icons';
import moment from 'moment';

import TinyEditor from '../../../ReportEditor/tinymce-editor';
import { getUserDetails, hasStudyViewingPermission, isValidDate } from '../../../../utils/helper';
import { useReportStatus } from '../../../../hooks/useReportStatus';
import { launchViewerOnDualMonitor } from '../../../../utils/MonitorManager';

import { useReportData } from '../../hooks/useReportData';
import { useTemplates } from '../../hooks/useTemplates';
import { useHeaderFooter } from '../../hooks/useHeaderFooter';
import { useReportSession } from '../../hooks/useReportSession';
import { useReportActions } from '../../hooks/useReportActions';
import { REPORT_CONFIG } from '../../constants';
import { isContentEmpty } from '../../utils/reportParser';
import type { OrderDetails, Report } from '../../types';

import EditorToolbar from './EditorToolbar';
import StatusActions from './StatusActions';
import ReportHistory from '../Panels/ReportHistory';
import PriorReports from '../Panels/PriorReports';
import PatientNotes from '../Panels/PatientNotes';
import AdvancedOptions from '../Panels/AdvancedOptions';
import TemplateLoadDialog from '../Dialogs/TemplateLoadDialog';
import CriticalFindingDialog from '../Dialogs/CriticalFindingDialog';

import '../../styles/editor.css';

interface ReportEditorProps {
  patientDetails: OrderDetails;
  handlePostSaving?: (status: string, patDetails: OrderDetails) => void;
  cancel: () => void;
}

const ReportEditor: React.FC<ReportEditorProps> = ({ patientDetails, handlePostSaving, cancel }) => {
  const editorRef = useRef<any>(null);
  const [content, setContent] = useState<string | null>(null);
  const [proxyUser, setProxyUser] = useState<string | null>(null);
  const [cosigningDoctor, setCosigningDoctor] = useState<string | null>(null);
  const [loadModalVisible, setLoadModalVisible] = useState(false);
  const [pendingHtml, setPendingHtml] = useState('');
  const [editorInitialized, setEditorInitialized] = useState(false);
  const [criticalFindingVisible, setCriticalFindingVisible] = useState(false);

  const [expandedSections, setExpandedSections] = useState({
    reportsHistory: true,
    priorReports: false,
    documents: false,
    advancedOptions: false,
  });

  const { pacs_order } = patientDetails;
  const { patient } = pacs_order;

  // ── Hooks ──
  const { statusList, loading: statusLoading } = useReportStatus();
  const {
    reports, currentReport, setCurrentReport,
    priorReports, notes, radUsers, allUsers,
    isSignedOff, refetchReports,
  } = useReportData(patientDetails);
  const {
    nodeOptions, templateOptions,
    selectedNode, selectedTemplate, setSelectedTemplate,
    handleNodeChange, resetSelections,
  } = useTemplates(pacs_order.po_modality, pacs_order.po_body_part);
  const { headerHtml, footerHtml, showFooter, configLoaded } = useHeaderFooter(patientDetails, currentReport);

  const onSaved = useCallback((status: string) => {
    message.success('Updated successfully');
    refetchReports();
  }, [refetchReports]);

  const { saveReport, deleteReport, printReport } = useReportActions({
    orderDetails: patientDetails,
    currentReport,
    isSignedOff,
    showFooter,
    onSaved,
  });
  useReportSession(pacs_order.pacs_ord_id);

  // ── Reset template selections when order changes ──
  useEffect(() => { resetSelections(); }, [patientDetails]);

  // ── Reconstruct content from current report parts ──
  useEffect(() => {
    if (!currentReport || !configLoaded) return;
    const { pr_report_header: h, pr_report_html: body, pr_report_footer: f } = currentReport;
    if (h && f) {
      const footer = showFooter ? f : '';
      setContent(`${h}${body}${footer}`);
    } else if (h) {
      setContent(`${h}${body}`);
    } else if (f) {
      setContent(`${body}${f}`);
    } else {
      setContent(body || '');
    }
  }, [currentReport, configLoaded, showFooter]);

  // ── Auto-select first template when editor is empty ──
  useEffect(() => {
    if (!templateOptions.length || selectedTemplate) return;
    if (!editorInitialized) return;
    if (!REPORT_CONFIG.AUTO_LOAD_TEMPLATE_WHEN_EMPTY) return;
    if (window.ENV_CONFIG?.DISABLE_TEMPLATE_AUTO_SELECTION) return;
    if (currentReport?.pr_report_html !== '<div></div>') return;

    const first = templateOptions[0];
    if (first) {
      setSelectedTemplate(first.value);
      showLoadConfirmation(first.templateHtml);
    }
  }, [templateOptions, selectedTemplate, currentReport?.pr_report_html, editorInitialized]);

  // ── Handlers ──
  const handleContentChange = useCallback((newContent: string) => {
    setContent(newContent);
    if (!editorInitialized) setEditorInitialized(true);
  }, [editorInitialized]);

  const handleSaveForm = useCallback((statusCode: string) => {
    const currentContent = editorRef.current?.getContent() || content;
    if (!currentContent) return;
    saveReport(currentContent, statusCode, {
      proxy_user: proxyUser,
      co_signing_doctor: cosigningDoctor,
    });
  }, [content, saveReport, proxyUser, cosigningDoctor]);

  const handleSaveShortCut = useCallback(() => {
    handleSaveForm(currentReport?.pr_status || 'DRAFTED');
  }, [handleSaveForm, currentReport?.pr_status]);

  const handlePrint = useCallback(() => {
    const currentContent = editorRef.current?.getContent() || content;
    if (currentContent) printReport(currentContent);
  }, [content, printReport]);

  const handleCosigning = useCallback((val: string, userData: any) => {
    setCosigningDoctor(val);
    if (userData?.user_signature) {
      setCurrentReport(prev => prev ? {
        ...prev,
        pr_report_html: `${prev.pr_report_html}\n\n<div id="cosign">${userData.user_signature}</div>`,
      } : prev);
    }
  }, [setCurrentReport]);

  const showLoadConfirmation = useCallback((newHtml: string) => {
    if (isContentEmpty(content)) {
      setCurrentReport(prev => ({
        ...prev!,
        pr_id: undefined,
        pr_report_html: newHtml,
        pr_status: 'DRAFTED',
      }));
      setContent(newHtml);
    } else {
      setPendingHtml(newHtml);
      setLoadModalVisible(true);
    }
  }, [content, setCurrentReport]);

  const handleTemplateChange = useCallback((val: string | null, templateHtml?: string) => {
    setSelectedTemplate(val);
    if (templateHtml) showLoadConfirmation(templateHtml);
  }, [showLoadConfirmation, setSelectedTemplate]);

  const handleLoadReport = useCallback((report: Report) => { setCurrentReport(report); }, [setCurrentReport]);

  const handleLoadPriorReport = useCallback((report: Report) => {
    showLoadConfirmation(report.pr_report_html || '<div></div>');
  }, [showLoadConfirmation]);

  const handleDeleteReport = useCallback(async (report: Report) => {
    await deleteReport(report);
    refetchReports();
  }, [deleteReport, refetchReports]);

  const toggleSection = (key: string) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="editor-container">
      {/* ── Left: Editor ── */}
      <div className="editor-section">
        <div className="section-header">
          <div className="section-header-title">Medical Report</div>
        </div>

        {isSignedOff && (
          <div style={{ backgroundColor: '#FFF7E6', border: '1px solid #FFD591', borderRadius: '4px', padding: '12px 16px', margin: '12px 16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ExclamationCircleOutlined style={{ color: '#FA8C16', fontSize: '16px' }} />
            <span style={{ color: '#873800', fontSize: '14px', fontWeight: 500 }}>
              Editing Signed-off Report: Template and history loading options are disabled.
            </span>
          </div>
        )}

        <div className="section-content">
          {configLoaded && currentReport ? (
            <div id="editor-container">
              <TinyEditor
                ref={editorRef}
                fromReporting
                initialContent={content || '<div></div>'}
                placeholder="placeholder..."
                handleChange={handleContentChange}
                headerContent={headerHtml}
                footerContent={footerHtml}
                handleSaveShortCut={handleSaveShortCut}
              />
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              Loading editor...
            </div>
          )}
        </div>

        <div className="section-footer">
          <EditorToolbar
            nodeOptions={nodeOptions}
            templateOptions={templateOptions}
            selectedNode={selectedNode}
            selectedTemplate={selectedTemplate}
            isSignedOff={isSignedOff}
            onNodeChange={handleNodeChange}
            onTemplateSelect={handleTemplateChange}
            onTranscription={(text, isPartial, replacePartial) => {
              editorRef.current?.handleTranscriptionText?.(text, isPartial, replacePartial);
            }}
          />

          <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.75rem' }}>
            <Button className="footer-btn" type="default" onClick={cancel}>Cancel</Button>
            {REPORT_CONFIG.SHOW_PRINT_BUTTON && (
              <Button className="footer-btn" type="default" onClick={handlePrint}>Print</Button>
            )}
            <StatusActions
              statusList={statusList}
              statusLoading={statusLoading}
              currentReportStatus={currentReport?.pr_status}
              isSignedOff={isSignedOff}
              onAction={handleSaveForm}
            />
          </div>
        </div>
      </div>

      {/* ── Right: Study Details & Panels ── */}
      <div className="control-section">
        <div className="section-header">
          <div className="section-header-title">Study Details</div>
        </div>
        <div className="section-content">
          {/* Patient Info Card */}
          <Card className="mb-3 patient-details-card">
            <div className="patient-info-line-1">
              <UserOutlined className="profile-icon" />
              <div className="pat-name">{patient?.pat_name}, {patient?.pat_pin || ''}</div>
            </div>
            <div className="patient-info-line-2">
              {patient?.pat_sex} / {patient?.pat_dob && isValidDate(patient.pat_dob) ? moment(patient.pat_dob).fromNow(true) : '-'}
            </div>
            <div className="patient-info-line-3">
              {pacs_order.po_modality} / {pacs_order.po_ref_doc || '-'}
            </div>
            {hasStudyViewingPermission(getUserDetails()) && (
              <Button
                className="launch-viewer-btn"
                type="default"
                onClick={async () => {
                  const w = await launchViewerOnDualMonitor(patientDetails.ps_study_uid, patientDetails);
                  if (!w) message.warning('Could not open viewer window');
                }}
              >
                Launch Viewer
              </Button>
            )}
          </Card>

          {/* Reports History */}
          <div className="collapsible-section">
            <div className="collapsible-header" onClick={() => toggleSection('reportsHistory')}>
              <div className="collapsible-header-left"><BarChartOutlined className="collapsible-icon" /><span className="collapsible-title">Reports History</span></div>
              {expandedSections.reportsHistory ? <UpOutlined className="collapsible-arrow" /> : <DownOutlined className="collapsible-arrow" />}
            </div>
            {expandedSections.reportsHistory && (
              <div className="collapsible-content" style={{ paddingTop: 0, paddingBottom: 0 }}>
                <ReportHistory reports={reports} isSignedOff={isSignedOff} onLoad={handleLoadReport} onDelete={handleDeleteReport} />
              </div>
            )}
          </div>

          {/* Prior Reports */}
          {priorReports.length > 0 && (
            <div className="collapsible-section">
              <div className="collapsible-header" onClick={() => toggleSection('priorReports')}>
                <div className="collapsible-header-left"><BarChartOutlined className="collapsible-icon" /><span className="collapsible-title">Prior Reports</span></div>
                {expandedSections.priorReports ? <UpOutlined className="collapsible-arrow" /> : <DownOutlined className="collapsible-arrow" />}
              </div>
              {expandedSections.priorReports && (
                <div className="collapsible-content" style={{ paddingTop: 0, paddingBottom: 0 }}>
                  <PriorReports reports={priorReports} isSignedOff={isSignedOff} onLoadAsTemplate={handleLoadPriorReport} />
                </div>
              )}
            </div>
          )}

          {/* Documents */}
          <div className="collapsible-section">
            <div className="collapsible-header" onClick={() => toggleSection('documents')}>
              <div className="collapsible-header-left"><FolderOutlined className="collapsible-icon" /><span className="collapsible-title">Documents</span></div>
              {expandedSections.documents ? <UpOutlined className="collapsible-arrow" /> : <DownOutlined className="collapsible-arrow" />}
            </div>
            {expandedSections.documents && (
              <div className="collapsible-content" style={{ paddingTop: 0, paddingBottom: 0, width: '101%' }}>
                <PatientNotes notes={notes} patient={patient} pacsOrder={pacs_order} />
              </div>
            )}
          </div>

          {/* Advanced Options */}
          {REPORT_CONFIG.SHOW_ADVANCED_OPTIONS && (
            <div className="collapsible-section">
              <div className="collapsible-header" onClick={() => toggleSection('advancedOptions')}>
                <div className="collapsible-header-left"><SettingOutlined className="collapsible-icon" /><span className="collapsible-title">Advanced Options</span></div>
                {expandedSections.advancedOptions ? <UpOutlined className="collapsible-arrow" /> : <DownOutlined className="collapsible-arrow" />}
              </div>
              {expandedSections.advancedOptions && (
                <AdvancedOptions
                  radUsers={radUsers}
                  allUsers={allUsers}
                  onNotifyPhysician={() => setCriticalFindingVisible(true)}
                  onCosignChange={handleCosigning}
                  onProxyUserChange={setProxyUser}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Dialogs ── */}
      <TemplateLoadDialog
        visible={loadModalVisible}
        onAppend={() => {
          setCurrentReport(prev => ({ ...prev!, pr_id: undefined, pr_report_html: (prev?.pr_report_html || '') + pendingHtml, pr_status: 'DRAFTED' }));
          setLoadModalVisible(false);
        }}
        onReplace={() => {
          setCurrentReport(prev => ({ ...prev!, pr_id: undefined, pr_report_html: pendingHtml, pr_status: 'DRAFTED' }));
          setLoadModalVisible(false);
        }}
        onCancel={() => setLoadModalVisible(false)}
      />

      <CriticalFindingDialog
        visible={criticalFindingVisible}
        orderDetails={patientDetails}
        onClose={() => setCriticalFindingVisible(false)}
      />
    </div>
  );
};

export default ReportEditor;
